import React, {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useObservable} from '@ngneat/react-rxjs';
import {useNavigate} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useTranslation} from 'react-i18next';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {emitOnce} from '@ngneat/elf';
import {useSnackbar} from 'notistack';
import {finalize, Observable} from 'rxjs';

import {ClientsUnit, clientsUnitsQuery, clientsUnitsService, searchClientsUnitsEffect} from '@store/clients/units';
import {GlobalStatus} from '@store/common/globalStatus.enum';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import ConfirmModal from '@components/modal/Confirm.modal';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {ClientUnitDetailsRoutes} from '@utils/routes.utils';

const ClientsUnitsTable = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const searchClientsUnits = useEffectFn(searchClientsUnitsEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{clientsUnits, loading}] = useObservable(clientsUnitsQuery.clientsUnits$);
  const [selectedClientsUnits] = useObservable(clientsUnitsQuery.selectedClientsUnits$);

  const [filterStatus] = useObservable(clientsUnitsQuery.status$);
  const [filters] = useObservable(clientsUnitsQuery.filters$);
  const [sortModel] = useObservable(clientsUnitsQuery.sort$);
  const [tablePagination] = useObservable(clientsUnitsQuery.pagination$);

  useEffect(() => {
    searchClientsUnits({status: filterStatus, filters, tablePagination, sort: sortModel});
  }, [filters, filterStatus, searchClientsUnits, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, clientUnit: ClientsUnit) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = clientsUnitsService.deleteClientsUnitInList(clientUnit.id);
        break;
      case OtherActionType.ARCHIVE:
        validationObservable = clientsUnitsService.updateClientsUnitsStatusInList(clientUnit.id, GlobalStatus.ARCHIVED);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.clientUnit`), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                clientsUnitsService.setFilters('search')(value);
                clientsUnitsService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={clientsUnitsService.setFilters('tableFilters')}
          handleReset={() => clientsUnitsService.resetFilters()}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.id',
            label: t('clients.clientUnitsTable.id'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.id,
            width: 100
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.name',
            label: t('clients.clientUnitsTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisation.name',
            label: t('clients.clientUnitsTable.client'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.organisationName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'invoicingAdditionalInformation',
            valueGetter: (params: GridValueGetterParams<ClientsUnit, ClientsUnit>) => params.row.invoicingAdditionalInformation,
            label: t('clients.clientUnitsTable.invoicingAdditionalInformation'),
            width: 350
          }),
          COLUMN_TYPES.TEXT({field: 'phone', label: t('clients.clientUnitsTable.phone'), width: 130}),
          COLUMN_TYPES.TEXT({
            field: 'email',
            color: 'secondary',
            label: t('clients.clientUnitsTable.email'),
            fullWidth: true,
            minWidth: 300
          }),
          COLUMN_TYPES.TEXT({
            field: 'address.city',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.city,
            label: t('clients.clientUnitsTable.city'), width: 250
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisationUnit.region',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.region,
            label: t('clients.clientUnitsTable.region'),
            width: 150
          }),
          COLUMN_TYPES.TEXT({
            field: 'address.country',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsUnit>) => params.row.country,
            label: t('clients.clientUnitsTable.country'),
            width: 150
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (clientUnit: ClientsUnit) => navigate(`${clientUnit.id}/${ClientUnitDetailsRoutes.CLIENT_UNIT}`),
            otherActions: [
              {
                type: OtherActionType.ARCHIVE,
                handleClick: (clientUnit: ClientsUnit) => setOpenConfirmAction({
                  type: OtherActionType.ARCHIVE,
                  row: clientUnit
                }),
                hidden: (clientUnit: ClientsUnit) => clientUnit.status === GlobalStatus.ARCHIVED || filterStatus === GlobalStatus.ARCHIVED,
              },
              {
                type: OtherActionType.DELETE,
                handleClick: (clientUnit: ClientsUnit) => setOpenConfirmAction({
                  type: OtherActionType.DELETE,
                  row: clientUnit
                }),
              },
            ]
          }),
        ]}
        handlePageSizeChange={clientsUnitsService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => clientsUnitsService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={clientsUnitsService.setSort}
        sortModel={[sortModel]}
        handleReload={() => searchClientsUnits({status: filterStatus, filters, tablePagination, sort: sortModel})}
        handleRowSelected={clientsUnitsService.setSelectedClientsUnits}
        rowsSelected={selectedClientsUnits}
        totalRows={clientsUnitsQuery.totalRows}
        data={clientsUnits}/>
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.clientUnit`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}/>
      )}
    </Stack>
  );
};

export default ClientsUnitsTable;
